/**
 * **Copy fix prompt** — one button, used on a row and on the detail page.
 *
 * It swaps its own label to *Copied* for a moment rather than raising a toast. The Studio's
 * God mode does this with a toast, and that is right there — one prompt, one overlay, one
 * message. Here the same button sits on every row of a table, and a toast per press would
 * put a floating message over the list you are working down. The label is where you are
 * already looking.
 *
 * **The fallback is not an error path.** `navigator.clipboard` does not exist in an insecure
 * context, and a browser may refuse the write outright — neither is the person's fault and
 * neither should lose them the text. Both land in a small dialog with the prompt selected,
 * ready for ⌘C. That is also why the failure is not a toast: a message saying "could not
 * reach the clipboard" would be true and useless.
 */

import { Button, Dialog } from '@atlas-nook/design-system';
import { useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import { ADMIN_STRINGS as S } from '../strings.js';

/** How long the label stays on *Copied*. Long enough to read, short enough not to nag. */
const COPIED_MS = 1600;

export function CopyPromptButton({
  text,
  label,
  children = S.issues.copyPrompt,
  size = 'md',
  isDisabled = false,
}: {
  /** The prompt to copy. Built by `fixPrompt.ts`, never here. */
  text: string;
  /**
   * The accessible name. It contains the visible label so the two agree (WCAG 2.5.3) and
   * adds what the visible one cannot fit — the id on a row, the count on the batch. A table
   * of identically named buttons is a screen-reader dead end.
   */
  label: string;
  /** The visible label. *Copy fix prompt* on a row; the batch says what it covers. */
  children?: ReactNode;
  size?: 'sm' | 'md';
  isDisabled?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [fallback, setFallback] = useState<string | null>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy(): Promise<void> {
    try {
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (clipboard?.writeText === undefined) throw new Error('no clipboard in this context');
      await clipboard.writeText(text);
      setCopied(true);
    } catch {
      setFallback(text);
    }
  }

  return (
    <>
      <Button
        tone="quiet"
        size={size}
        aria-label={copied ? undefined : label}
        isDisabled={isDisabled}
        onClick={() => void copy()}
      >
        {copied ? S.issues.copied : children}
      </Button>

      {fallback !== null && (
        <Dialog
          open
          onClose={() => setFallback(null)}
          title={S.issues.copyFallbackTitle}
          actions={
            <Button tone="primary" onClick={() => setFallback(null)}>
              {S.common.close}
            </Button>
          }
        >
          <p className="note">{S.issues.copyFallbackBody}</p>
          <SelectedText text={fallback} />
        </Dialog>
      )}
    </>
  );
}

/**
 * The prompt, selected the moment it appears, so the next keystroke is ⌘C.
 *
 * A raw `textarea` rather than the design system's `TextAreaField`: this one needs a ref to
 * select itself, and it is read-only output rather than a field anyone edits. It still wears
 * the sheet's `.textarea`, so it is the same box everywhere else in the app.
 */
function SelectedText({ text }: { text: string }): JSX.Element {
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    area.current?.focus();
    area.current?.select();
  }, []);

  return (
    <textarea
      ref={area}
      className="textarea"
      readOnly
      rows={12}
      aria-label={S.issues.copyFallbackLabel}
      value={text}
    />
  );
}
